import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { isEnPath, stripEnPrefix, withEnPrefix } from "@/lib/locale";
import { getEnMeta } from "@/data/seoMetaEn";

const BASE_URL = "https://www.naklejki-3d.com.pl";
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.jpg`;

interface SEOHeadProps {
  title?: string;
  description?: string;
  ogImage?: string;
  ogType?: "website" | "article";
  articleDate?: string;
  noindex?: boolean;
}

const SEOHead = ({
  title = "Producent naklejek 3D wypukłych – doming | B2B",
  description = "Producent naklejek 3D wypukłych (doming) dla firm. Trwałość ponad 15 lat, wycena online w 15 sekund, dowolny kształt i 14 folii.",
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  articleDate,
  noindex = false,
}: SEOHeadProps) => {
  const { pathname } = useLocation();
  const isEn = isEnPath(pathname);
  const polishPath = stripEnPrefix(pathname);
  const enMeta = isEn ? getEnMeta(polishPath) : undefined;
  const finalTitle = enMeta ? `${enMeta.title} | naklejki-3d.com.pl` : title;
  const finalDescription = enMeta ? enMeta.description : description;

  const withSlash = (p: string) => (p.endsWith("/") ? p : `${p}/`);
  const canonicalUrl = `${BASE_URL}${withSlash(pathname)}`;
  const plUrl = `${BASE_URL}${withSlash(polishPath)}`;
  const enUrl = `${BASE_URL}${withSlash(withEnPrefix(polishPath))}`;

  useEffect(() => {
    // Title
    document.title = finalTitle;

    // Helper to set/create meta tags
    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    // Basic meta
    setMeta("name", "description", finalDescription);
    setMeta(
      "name",
      "robots",
      noindex
        ? "noindex, nofollow"
        : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1",
    );

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);

    // Hreflang tags
    // Remove old hreflang
    document.querySelectorAll('link[hreflang]').forEach((el) => el.remove());
    const addAlternate = (lang: string, href: string) => {
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", lang);
      link.setAttribute("href", href);
      document.head.appendChild(link);
    };
    addAlternate("pl", plUrl);
    addAlternate("en", enUrl);
    addAlternate("x-default", plUrl);

    // Open Graph
    setMeta("property", "og:title", finalTitle);
    setMeta("property", "og:description", finalDescription);
    setMeta("property", "og:url", canonicalUrl);
    setMeta("property", "og:type", ogType);
    setMeta("property", "og:image", ogImage);
    setMeta("property", "og:image:width", "1200");
    setMeta("property", "og:image:height", "630");
    setMeta("property", "og:site_name", "naklejki-3d.com.pl");
    setMeta("property", "og:locale", isEn ? "en_GB" : "pl_PL");

    // Twitter Cards
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", finalTitle);
    setMeta("name", "twitter:description", finalDescription);
    setMeta("name", "twitter:image", ogImage);

    // Article date
    if (ogType === "article" && articleDate) {
      setMeta("property", "article:published_time", articleDate);
    }

    return () => {
      // Cleanup hreflang on unmount
      document.querySelectorAll('link[hreflang]').forEach((el) => el.remove());
    };
  }, [finalTitle, finalDescription, canonicalUrl, plUrl, enUrl, isEn, ogImage, ogType, articleDate, noindex]);

  return null;
};

export default SEOHead;
